import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertCircle, ShieldCheck, ExternalLink, Users } from "lucide-react";

export default function SubPayPage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [ctx, setCtx] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get(`/pay/sub/${token}`);
                if (alive) setCtx(data);
            } catch (e) {
                if (alive) setError(formatApiError(e));
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [token]);

    const goToCheckout = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/pay/sub/${token}/checkout`, {
                origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch (e) {
            setError(formatApiError(e));
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <PayLayout>
                <div className="text-navy-500 text-sm">Loading payment link…</div>
            </PayLayout>
        );
    }

    if (error) {
        return (
            <PayLayout>
                <div
                    data-testid="sub-pay-error"
                    className="rounded-2xl bg-white border border-red-200 shadow-soft p-8 max-w-md"
                >
                    <div className="flex items-center gap-2 text-red-600">
                        <AlertCircle />
                        <div className="font-display text-2xl">Link can't be used</div>
                    </div>
                    <p className="text-navy-500 text-sm mt-3">{error}</p>
                    <p className="text-navy-500 text-xs mt-3">
                        Please ask the primary member to send you a fresh link.
                    </p>
                    <Button
                        onClick={() => navigate("/")}
                        className="mt-6 rounded-full bg-navy-700 hover:bg-navy-800 text-white"
                    >
                        Back to Perx
                    </Button>
                </div>
            </PayLayout>
        );
    }

    return (
        <PayLayout>
            <div
                data-testid="sub-pay-card"
                className="rounded-2xl bg-white shadow-soft p-8 max-w-md w-full"
            >
                <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-2">
                    Perx invite
                </div>
                <h1 className="font-display text-3xl text-navy-700 leading-tight">
                    Hi {ctx.sub_user?.name?.split(" ")[0] || "there"} 🐼
                </h1>
                <p className="text-navy-500 text-sm mt-3">
                    <span className="text-navy-700 font-medium">
                        {ctx.primary?.name || "A Perx member"}
                    </span>{" "}
                    added you as a secondary member and wants you to activate your own
                    Perx subscription so you can start unlocking perks.
                </p>

                {ctx.deal ? (
                    <div className="rounded-xl bg-cream p-4 mt-6 border border-gold-200/60">
                        <div className="flex items-center gap-3">
                            {ctx.deal.image && (
                                <img
                                    src={ctx.deal.image}
                                    alt=""
                                    className="h-14 w-14 rounded-lg object-cover"
                                />
                            )}
                            <div>
                                <div className="font-display text-lg text-navy-700 leading-tight">
                                    {ctx.deal.title}
                                </div>
                                <div className="text-xs text-navy-500">
                                    {ctx.deal.vendor}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="rounded-xl bg-cream p-4 mt-6 border border-gold-200/60">
                        <div className="flex items-center gap-3">
                            <Users className="text-gold-600" />
                            <div>
                                <div className="font-display text-lg text-navy-700 leading-tight">
                                    Perx subscription
                                </div>
                                <div className="text-xs text-navy-500">
                                    Billed monthly to your own card
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <Button
                    onClick={goToCheckout}
                    disabled={busy}
                    data-testid="sub-pay-checkout"
                    className="w-full rounded-full bg-gold-500 hover:bg-gold-600 text-navy-900 font-semibold h-12 mt-6"
                >
                    {busy ? "Redirecting to Stripe…" : "Continue to secure payment"}
                    <ExternalLink size={14} className="ml-2" />
                </Button>

                <div className="flex items-center gap-2 text-[11px] text-navy-500 mt-4 justify-center">
                    <ShieldCheck size={12} className="text-emerald-600" /> Secured by
                    Stripe · Single-use link
                </div>
            </div>
        </PayLayout>
    );
}

function PayLayout({ children }) {
    return (
        <div className="min-h-screen bg-navy-700 flex items-center justify-center px-4 py-10">
            <div className="w-full max-w-md flex flex-col items-center gap-6">
                <div className="text-center">
                    <div className="font-display text-3xl text-white">Perx Group</div>
                    <div className="text-xs uppercase tracking-[0.3em] text-gold-500 mt-1">
                        Member payment
                    </div>
                </div>
                {children}
            </div>
        </div>
    );
}
